import React, { Fragment, useEffect, useState } from "react";
import { object, func, number } from "prop-types";
import { connect } from "react-redux";
import DailyIframe from "@daily-co/daily-js";
import history from "../history";

import { Button, Loading } from "../common";

import {
  rightToWorkVerificationQueuePosition,
  rightToWorkVerificationQueueRegister,
  rightToWorkVerificationQueueSize,
  rightToWorkVerificationFinished,
} from "../redux/actions/rtwActions";
import PulseLoader from "../common/PulseLoader";

const RightToWorkVerification = ({
  rtwVerificationCall,
  requestQueueSize,
  requestJoinCall,
  requestQueuePosition,
  requestVerificationFinished,
  leaveQueue,
  nextStepLink,
}) => {
  const initialState = {
    waitingInQueue: false,
    callInProgress: false,
    callFinished: false,
  };

  const [callState, setCallState] = useState(initialState);

  const handleLeaveQueue = () => {
    leaveQueue(false);
  };

  const requestQueuePositionWrapper = (waitingInQueue) => {
    if (waitingInQueue) {
      requestQueuePosition();
    }
  };

  const joinQueue = () => {
    requestJoinCall();
    setCallState({ ...callState, waitingInQueue: true });
  };

  useEffect(() => {
    joinQueue();
    if (!rtwVerificationCall.isLoading) {
      requestQueueSize();
    }
  }, []);

  useEffect(() => {
    const requestQueuePositionHandle = setInterval(
      () => requestQueuePositionWrapper(callState.waitingInQueue),
      30000
    );
    return () => clearInterval(requestQueuePositionHandle);
  }, [callState.waitingInQueue]);

  const callFinished = (callFrame) => {
    setCallState({ ...callState, callFinished: true, callInProgress: false });
    callFrame.destroy();
    requestVerificationFinished();
  };

  const joinCall = () => {
    const callFrame = DailyIframe.createFrame(
      document.getElementById("call-frame-container"),
      {
        showLeaveButton: true,
        showFullscreenButton: true,
      }
    );
    callFrame.on("left-meeting", () => callFinished(callFrame));
    setCallState({ ...callState, callInProgress: true, waitingInQueue: false });
    callFrame.join({ url: rtwVerificationCall.connectionLink });
  };

  const CurrentQueueSize = () => (
    <div>
      Currently there is {rtwVerificationCall.queueSize}{" "}
      {rtwVerificationCall.queueSize === 1 ? "person" : "people"} in the queue
      for a video call
    </div>
  );

  const Loader = () => (
    <div className="w-100 d-flex flex-column align-items-center justify-content-center">
      <PulseLoader />
    </div>
  );

  const ordinalSuffix = (n) => {
    return ["st", "nd", "rd"][((((n + 90) % 100) - 10) % 10) - 1] || "th";
  };

  const CurrentQueuePosition = () => (
    <div className="text-center">
      {rtwVerificationCall.candidatePosition && (
        <span>
          You are currently {rtwVerificationCall.candidatePosition}
          {ordinalSuffix(rtwVerificationCall.candidatePosition)} in the queue.
          Please wait a HRGO operator will be with you soon.
        </span>
      )}
      <Loader />
    </div>
  );

  const JoinQueueButton = () => (
    <div>
      <Button
        classes="btn btn-primary btn-block mb-2"
        styles={{ width: 264, margin: "auto" }}
        onClick={joinQueue}
        disabled={rtwVerificationCall.isLoading}
      >
        {" "}
        {rtwVerificationCall.isLoading ? <Loading /> : "Join the queue"}
      </Button>
    </div>
  );

  const JoinCallInvite = () => (
    <div>
      Please click the button below to begin the video call with our HRGO
      operator.
      <Button
        classes="btn btn-primary btn-block my-2"
        styles={{ width: 264, margin: "auto" }}
        onClick={joinCall}
      >
        {"Start video call"}
      </Button>
      <Button
        classes="btn btn-outline-info btn-block text-dark my-2"
        styles={{ width: 264, margin: "auto" }}
        onClick={handleLeaveQueue}
      >
        Back
      </Button>
    </div>
  );

  const CallFinished = () => (
    <div>
      <p>Thank you for joining our Right to Work video verification call.</p>
      <p>
        If your Right to Work was verified successfully by one of our operators,
        your local branch will be informed and they will contact you for next
        steps.
      </p>
      <p>
        If your Right to Work was unable to be verified by one of our operators,
        please follow the instructions they told you on the call.
      </p>
      <p>
        If you were unable to connect successfully to the call, please check
        your inbox for an email containing helpful information for you to try
        and rejoin.
      </p>
      <div className={"mt-1 lg-mt-2"}>
        <Button
          classes="btn btn-primary btn-block my-2"
          styles={{ width: 264, margin: "auto" }}
          onClick={() => history.push(nextStepLink)}
        >
          Next
        </Button>
        <Button
          classes="btn btn-outline-info btn-block text-dark my-2"
          styles={{ width: 264, margin: "auto" }}
          onClick={handleLeaveQueue}
        >
          Back
        </Button>
      </div>
    </div>
  );

  const displayCurrentQueueSize = () =>
    !rtwVerificationCall.isLoading &&
    !rtwVerificationCall.connectionLink &&
    !callState.waitingInQueue;

  const displayJoinQueueButton = () =>
    !rtwVerificationCall.token && !callState.waitingInQueue;

  const displayCurrentQueuePosition = () =>
    callState.waitingInQueue &&
    !rtwVerificationCall.connectionLink &&
    !callState.callInProgress &&
    !callState.callFinished;

  const displayJoinCallInvite = () =>
    rtwVerificationCall.connectionLink &&
    !callState.callInProgress &&
    !rtwVerificationCall.verificationFinished;

  const displayCallFinishedMessage = () =>
    callState.callFinished || rtwVerificationCall.verificationFinished;

  const displayCallContainer = () =>
    !rtwVerificationCall.outOfOffice && !callState.callFinished;

  const initialLoadInProgress = () =>
    Object.keys(rtwVerificationCall).length === 1;

  const CallActions = () => (
    <>
      <div className="mt-1 mt-lg-3 text-center">
        {displayCurrentQueueSize() && <CurrentQueueSize />}
        {displayJoinQueueButton() && <JoinQueueButton />}
        {displayCurrentQueuePosition() && <CurrentQueuePosition />}
        {displayJoinCallInvite() && <JoinCallInvite />}
      </div>
      <div className={"mt-1 mt-lg-0"}>
        {displayCallFinishedMessage() && <CallFinished />}
      </div>
    </>
  );

  const OutOfOffice = () => (
    <div className="mt-1 mt-lg-3 text-center">
      We will need to conduct a video call with you to verify your
      documentation. Please come back to this section{" "}
      {rtwVerificationCall.availableTime} to join a call.
      <div className={"mt-1 lg-mt-2"}>
        <Button
          classes="btn btn-primary btn-block my-2"
          styles={{ width: 264, margin: "auto" }}
          onClick={() => history.push(nextStepLink)}
        >
          Next
        </Button>
      </div>
    </div>
  );

  return (
    <>
      {initialLoadInProgress() && (
        <div className={"mt-1 mt-lg-3"}>
          <Loader />
        </div>
      )}
      {!initialLoadInProgress() && rtwVerificationCall.outOfOffice && (
        <OutOfOffice />
      )}
      {!initialLoadInProgress() &&
        !rtwVerificationCall.outOfOffice &&
        !callState.callInProgress && <CallActions />}
      {callState.callInProgress && displayCallContainer() && (
        <div className={"mt-lg-n2 mb-2"}>
          Please wait on this page for one of our HRGO operators to connect with
          you, this could take up to a few minutes. You are in a call queue and
          an operator is aware you are waiting. Thank you for your patience.
        </div>
      )}
      {displayCallContainer() && (
        <div
          className={"mb-5 mb-lg-0"}
          id="call-frame-container"
          style={{ height: callState.callInProgress ? "450px" : "0px" }}
        ></div>
      )}
    </>
  );
};

RightToWorkVerification.propTypes = {
  rtwVerificationCall: object,
  requestQueueSize: func,
  requestQueuePosition: func,
  requestJoinCall: func,
  requestVerificationFinished: func,
  leaveQueue: func,
};

const mapStateToProps = ({ core: { rtwVerificationCall } }) => ({
  rtwVerificationCall,
});

const mapDispatchToProps = {
  requestQueueSize: rightToWorkVerificationQueueSize,
  requestJoinCall: rightToWorkVerificationQueueRegister,
  requestQueuePosition: rightToWorkVerificationQueuePosition,
  requestVerificationFinished: rightToWorkVerificationFinished,
};

const withRedux = connect(mapStateToProps, mapDispatchToProps);

export default withRedux(RightToWorkVerification);
